/**
 * WebRTC peer connections (Ratomeet signaling)
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import config from './config';

export const useWebRTC = (socketApi, roomId, localStream) => {
  const [remoteStreams, setRemoteStreams] = useState({});
  const peerConnections = useRef({});
  const pendingCandidates = useRef({});
  const roomIdRef = useRef(roomId);
  const localStreamRef = useRef(localStream);

  roomIdRef.current = roomId;
  localStreamRef.current = localStream;

  const createPeerConnection = useCallback((userId, userName) => {
    if (peerConnections.current[userId]) {
      return peerConnections.current[userId];
    }

    const pc = new RTCPeerConnection(config.ICE_SERVERS);
    const stream = localStreamRef.current;

    if (stream) {
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });
    }

    pc.onicecandidate = (event) => {
      if (event.candidate && socketApi?.isConnected) {
        socketApi.sendIceCandidate(event.candidate, userId, roomIdRef.current);
      }
    };

    pc.ontrack = (event) => {
      if (event.streams?.[0]) {
        setRemoteStreams((prev) => ({
          ...prev,
          [userId]: event.streams[0],
        }));
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'failed') {
        pc.restartIce();
      }
    };

    peerConnections.current[userId] = pc;

    if (pendingCandidates.current[userId]) {
      pendingCandidates.current[userId].forEach((candidate) => {
        pc.addIceCandidate(new RTCIceCandidate(candidate)).catch(() => {});
      });
      delete pendingCandidates.current[userId];
    }

    return pc;
  }, [socketApi]);

  const createOffer = useCallback(
    async (userId, userName) => {
      if (!roomIdRef.current || !socketApi?.isConnected) return;
      if (peerConnections.current[userId]?.signalingState !== 'stable' &&
          peerConnections.current[userId]?.localDescription) {
        return;
      }

      const pc = createPeerConnection(userId, userName);
      try {
        const offer = await pc.createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        });
        await pc.setLocalDescription(offer);
        socketApi.sendOffer(offer, userId, roomIdRef.current);
      } catch (err) {
        console.error('[Ratomeet] Error creating offer:', err);
      }
    },
    [createPeerConnection, socketApi]
  );

  const handleOffer = useCallback(
    async (data) => {
      const { offer, from, userName } = data;
      if (!from || !offer) return;

      const pc = createPeerConnection(from, userName || 'Participant');
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        if (socketApi?.isConnected) {
          socketApi.sendAnswer(answer, from, roomIdRef.current);
        }
      } catch (err) {
        console.error('[Ratomeet] Error handling offer:', err);
      }
    },
    [createPeerConnection, socketApi]
  );

  const handleAnswer = useCallback(async (data) => {
    const { answer, from } = data;
    const pc = peerConnections.current[from];
    if (pc && answer) {
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
      } catch (err) {
        console.error('[Ratomeet] Error handling answer:', err);
      }
    }
  }, []);

  const handleIceCandidate = useCallback(async (data) => {
    const { candidate, from } = data;
    if (!from || !candidate) return;
    const pc = peerConnections.current[from];
    if (pc && pc.remoteDescription) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('[Ratomeet] Error adding ICE candidate:', err);
      }
    } else {
      if (!pendingCandidates.current[from]) {
        pendingCandidates.current[from] = [];
      }
      pendingCandidates.current[from].push(candidate);
    }
  }, []);

  const handleUserLeft = useCallback((data) => {
    const { socketId } = data;
    if (peerConnections.current[socketId]) {
      peerConnections.current[socketId].close();
      delete peerConnections.current[socketId];
    }
    setRemoteStreams((prev) => {
      const updated = { ...prev };
      delete updated[socketId];
      return updated;
    });
  }, []);

  const handleExistingParticipants = useCallback(
    (participants) => {
      if (!Array.isArray(participants)) return;
      participants.forEach((participant) => {
        createOffer(participant.socketId, participant.userName);
      });
    },
    [createOffer]
  );

  const cleanup = useCallback(() => {
    Object.values(peerConnections.current).forEach((pc) => pc.close());
    peerConnections.current = {};
    pendingCandidates.current = {};
    setRemoteStreams({});
  }, []);

  useEffect(() => {
    if (!socketApi?.isConnected) return;

    socketApi.on('offer', handleOffer);
    socketApi.on('answer', handleAnswer);
    socketApi.on('ice-candidate', handleIceCandidate);
    socketApi.on('user-left', handleUserLeft);
    socketApi.on('existing-participants', handleExistingParticipants);

    return () => {
      socketApi.off('offer', handleOffer);
      socketApi.off('answer', handleAnswer);
      socketApi.off('ice-candidate', handleIceCandidate);
      socketApi.off('user-left', handleUserLeft);
      socketApi.off('existing-participants', handleExistingParticipants);
    };
  }, [
    socketApi,
    socketApi?.isConnected,
    handleOffer,
    handleAnswer,
    handleIceCandidate,
    handleUserLeft,
    handleExistingParticipants,
  ]);

  useEffect(() => {
    if (!localStream) return;
    Object.values(peerConnections.current).forEach((pc) => {
      const senders = pc.getSenders();
      localStream.getTracks().forEach((track) => {
        const sender = senders.find((s) => s.track?.kind === track.kind);
        if (sender) {
          sender.replaceTrack(track).catch(() => {});
        } else {
          pc.addTrack(track, localStream);
        }
      });
    });
  }, [localStream]);

  return {
    remoteStreams,
    cleanup,
  };
};

export default useWebRTC;
